import React, { useMemo, useState } from 'react';

import copy from 'copy-to-clipboard';
import Formsy from 'formsy-react';
import { kebabCase } from 'lodash';
import { ChevronDown, Copy, Trash } from 'lucide-react';
import { Row, Col, Button, Label, FormGroup, SheetFooter, SheetBody, Input as TWInput, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, CardHeader, CardTitle, Separator, DefaultTooltip, ButtonGroup } from 'twyne-new.ui';

import InfoTip from '@Components/InfoTip';
import InputTip from '@Components/InputTip';
import { useParams } from '@Components/RouterProvider';
import Select from '@Components/form/Select';
import Input from 'components/form/Input';
import Switch from 'components/form/Switch';

import { useRewardStore } from '../../../store';
import { useRewardStepsStore } from '../store';

import URLView from './CopyUrl';
import { copyAsJson, curlJson, curlUrlencoded, PAYLOAD_KEYS } from './utils';

const URL_ORIGIN = new URL(window.location)
URL_ORIGIN.pathname = '/reward/activity'
if (URL_ORIGIN.port) {
  URL_ORIGIN.port = 3000
}

export default class StepForm extends React.Component {
  state = {
    active: true,
    transId: '',
    transAmount: '',
    activityName: '',
    activityId: '',
    status: '',
    optionalParams: {},
    order: null,
  }

  componentDidMount() {
    const { data } = this.props
    this.setState({
      ...data,
      rId: data.rewardId,
      transId: '{peKey}',
      transAmount: '{money}',
      activityName: '{activityNameExample}',
      activityId: '{activityIdExample}',
      status: 'completed | pending | rejected',
    })
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data?.id !== this.props.data?.id) {
      const { data } = this.props
      this.setState({
        ...data,
        rId: data.rewardId,
        transId: '{peKey}',
        transAmount: '{money}',
        activityName: '{activityNameExample}',
        activityId: '{activityIdExample}',
        status: 'completed | pending | rejected',
      })
      return
    }

    if (prevProps.data !== this.props.data) {
      this.setState({
        transId: '{peKey}',
        transAmount: '{money}',
        activityName: '{activityNameExample}',
        activityId: '{activityIdExample}',
        status: 'completed | pending | rejected',
      })
    }
  }

  handleChangeName = e => {
    if (this.props.data?.slug) {
      return
    }
    const { value } = e.target
    this.setState({ slug: kebabCase(value) })
  }

  handleValidSubmit = model => {
    this.props.handleSubmit({ ...this.state, ...model })
  }

  handleCancel = () => {
    this.props.onCancel()
  }

  validateSlug = ({ slug }) => {
    const { rewardSteps } = this.props.data
    return !rewardSteps.some(step => step.id !== this.state.id && step.slug === slug)
  }

  handleChangeSlug = e => {
    const { value } = e.target
    this.setState({
      slug: `${value}`
        .replace(/[^\w-]/g, '')
        .replace(/\s+/g, '-'),
    })
  }

  PagesSelect = () => {
    const { lists: { pages: { useLoad, items } } } = useRewardStepsStore()
    const { mode } = this.state

    const params = useMemo(() => {
      if (mode === 'offers') {
        return { type: 'partner-twyne' }
      }
      return {}
    }, [mode])

    useLoad(params)

    const options = useMemo(() => items.map(({ id, name, type }) => ({ label: `[#${id}][${type}] ${name}`, value: id })), [items])

    return <Select name="pageIds" value={this.state.pageIds} options={options} multi handleOnChange={selected => this.setState({ pageIds: selected ? selected.map(({ value }) => value) : null })} required />
  }

  OptionalParams = () => <>
    <DropdownMenu size='sm'>
      <DropdownMenuTrigger>
        <Button variant='light' size='sm'>
          Optional Params
          <ChevronDown size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {PAYLOAD_KEYS.map(([key, _tip, _valueTip, required]) => !required && <DropdownMenuItem disabled={this.state.optionalParams[key]} key={key} onClick={() => this.setState({ optionalParams: { ...this.state.optionalParams, [key]: true } })}>{key}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  </>

  PayloadPreview = () => {
    const { mode } = this.state
    const [copied, setCopied] = useState(false)
    const { stepId: id } = useParams()

    const handleClick = () => {
      copy(copyAsJson({ ...this.state, status: 'completed' }))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    if (!['offers', 'external_page'].includes(mode) || !id) {
      return null
    }

    return <>
      <Col size="12">
        <CardHeader className='justify-between'>
          <div className='flex gap-2'>
            <CardTitle>
              Payload
            </CardTitle>
            <InfoTip side='right'>
              The payload separated by key and value. Only x-www-form-urlencoded and json are supported content types. You can copy one by one or copy the full curl.
            </InfoTip>
          </div>
          <div className="flex gap-2">
            <this.OptionalParams />
            <ButtonGroup>
              <DefaultTooltip content='Copied!' open={copied}>
                <DefaultTooltip content={!copied && 'Click to copy the payload as JSON'} triggerProps={{ asChild: true }}>
                  <Button size="sm" variant='outline' color="accent" onClick={handleClick}>Copy as JSON</Button>
                </DefaultTooltip>
              </DefaultTooltip>
              <Button size="sm" color="accent" variant='light' onClick={() => this.setState({
                transId: '{peKey}',
                transAmount: '{money}',
                activityName: '{activityNameExample}',
                activityId: '{activityIdExample}',
                status: 'completed | pending | rejected',
              })}>
                Reset
              </Button>
            </ButtonGroup>
          </div>
        </CardHeader>
      </Col>
      <Col size="12" className='flex flex-col gap-2'>
        {PAYLOAD_KEYS.map(([key, tip, valueTip, required]) => (required || this.state.optionalParams[key]) && <>
          <Row key={key}>
            <Col size="3">
              <InputTip icon={<InfoTip />} tip={tip}>
                <TWInput value={key} disabled />
              </InputTip>
            </Col>
            <Col size="9" className='flex items-center gap-2'>
              <div className='w-full'>
                <InputTip tipProps={{ side: 'left' }} icon={<InfoTip />} id={`${key}-value-tip`} tip={valueTip}>
                  <TWInput value={this.state[key === 'stepId' ? 'id' : key]} onChange={({ target: { value } }) => this.setState({ [key === 'stepId' ? 'stepId' : key]: value })} disabled={['rId', 'stepId', 'slug'].includes(key)} />
                </InputTip>
              </div>
              {this.state.optionalParams[key] && <Button variant='outline' size='sm' onClick={() => this.setState({ optionalParams: { ...this.state.optionalParams, [key]: false } })}>
                <Trash size={16} />
              </Button>}
            </Col>
          </Row>
        </>)}
      </Col>
    </>
  }

  CopyActions = ({ onClick }) => {
    const handleClick = type => {
      switch (type) {
        case 'url':
          copy(URL_ORIGIN)
          break
        case 'formUrlencoded':
          copy(curlUrlencoded({ ...this.state, url: URL_ORIGIN, status: 'completed' }))
          break
        case 'json':
          copy(curlJson({ ...this.state, url: URL_ORIGIN, status: 'completed' }))
          break
      }
      onClick()
    }

    return <DropdownMenu size='sm'>
      <DropdownMenuTrigger>
        <Button variant='light'>
          <Copy size={16} />
          Copy
          <ChevronDown size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => handleClick('url')}>Only URL</DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleClick('formUrlencoded')}>curl URL <small>x-www-form-urlencoded</small></DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleClick('json')}>curl URL <small>json</small></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  }

  CopyUrl = () => {
    const { mode } = this.state
    const { stepId: id } = useParams()
    const [copied, setCopied] = useState(false)

    const url = new URL('', URL_ORIGIN)
    url.host = URL_ORIGIN.host
    url.protocol = URL_ORIGIN.protocol
    url.pathname = `reward/activity`

    if (!['offers', 'external_page'].includes(mode) || !id) {
      return null
    }

    const handleClickOverrideAction = () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    return <Col size="12">
      <FormGroup>
        <Label>
          Offers Url
          <InfoTip side='right'>
            When a user clicks on an offer, you must call this URL with a valid status (completed, pending, or rejected). Only the completed status is considered for required offers. Make sure this URL is called in user interactions with your offers.
          </InfoTip>
        </Label>
        <URLView value={url} actions={this.CopyActions} isCopied={copied} actionsProps={{ onClick: handleClickOverrideAction }}>
          <Button variant='light'>
            POST
          </Button>
        </URLView>
      </FormGroup>
    </Col>
  }

  Points = () => {
    const { collection: { item: reward } } = useRewardStore()

    if (['deals_only', 'master_cashout'].includes(reward?.type)) return null

    return <FormGroup>
      <Label>Points</Label>
      <TWInput name="points" type="number" value={this.state.points || ''} onChange={({ target: { value } }) => this.setState({ points: value >= 0 ? value : this.state.points })} required />
    </FormGroup>
  }

  Mode = () => {
    const { collection: { item: reward } } = useRewardStore()

    const options = useMemo(() => {
      if (reward?.type === 'points') {
        return [{ label: 'Progression', value: 'progression' }, { label: 'Offers', value: 'offers' }, { label: 'External Page', value: 'external_page' }]
      }
      return [{ label: 'offers', value: 'offers' }, { label: 'External Page', value: 'external_page' }]
    }, [reward?.type])

    return <Col size="6">
      <FormGroup>
        <Label>Mode</Label>
        <Select name="mode" value={this.state.mode || ''} required options={options} handleOnChange={option => {
          const data = { mode: option?.value }

          if (!['offers', 'external_page'].includes(option?.value)) {
            data.offersRequired = null
          }
          data.pageIds = null

          this.setState(data)
        }} />
      </FormGroup>
    </Col>
  }

  render() {
    return (
      <Formsy onValidSubmit={this.handleValidSubmit}>
        <SheetBody>
          <Row>
            <Col size="6">
              <FormGroup>
                <Label>Name</Label>
                <Input name="name" value={this.state.name || ''} required onChange={this.handleChangeName} />
              </FormGroup>
            </Col>
            <Col size="3">
              <FormGroup>
                <Label>Slug</Label>
                <Input name="slug" validations={{ validateSlug: this.validateSlug }} validationErrors={{ validateSlug: 'This slug already exists.' }} value={this.state.slug || ''} required onChange={this.handleChangeSlug} />
              </FormGroup>
            </Col>
            <Col size="3">
              <this.Points />
            </Col>
            <Col size="10">
              <FormGroup>
                <Label>Description</Label>
                <Input name="description" value={this.state.description || ''} required />
              </FormGroup>
            </Col>
            <Col size='2'>
              <FormGroup>
                <Label>Active</Label>
                <Switch name="active" value={this.state.active || false} />
              </FormGroup>
            </Col>
            <this.Mode />
            {['offers', 'external_page'].includes(this.state.mode) && <>
              <Col size="6">
                <FormGroup>
                  <Label>Offers required</Label>
                  <TWInput name="offersRequired" type="number" value={this.state.offersRequired || ''} onChange={({ target: { value } }) => this.setState({ offersRequired: value >= 0 ? value : this.state.offersRequired })} required />
                </FormGroup>
              </Col>
            </>}
            {['offers', 'external_page'].includes(this.state.mode) && <>
              <Col>
                <FormGroup>
                  <Label>Page</Label>
                  <this.PagesSelect />
                </FormGroup>
              </Col>
            </>}
            {['offers', 'external_page'].includes(this.state.mode) && <>
              <Col>
                <FormGroup>
                  <Label>
                    Lockout
                    <InfoTip side='right'>
                      If enabled, the user will not be able to progress to the next page on the midpath until the required offers are completed.
                    </InfoTip>
                  </Label>
                  <Switch name="lockout" value={this.state.lockout || false} />
                </FormGroup>
              </Col>
            </>}
            <this.CopyUrl />
            <this.PayloadPreview />
          </Row>
        </SheetBody>
        <SheetFooter>
          <Button onClick={this.handleCancel} variant='ghost'>Cancel</Button>
          <Button type="submit" color="accent" >Save</Button>
        </SheetFooter>
      </Formsy>
    );
  }
}
